import { showToast } from '../utils/toast.js';

/**
 * FilePdfMerge — handles checkbox selection and "Generate PDF" button
 * in the #files_box of any request detail panel.
 *
 * Works with delegated events so it functions inside dynamically loaded panels.
 */
export class FilePdfMerge {
    init() {
        document.addEventListener('change', (e) => {
            const cb = e.target.closest('.file-pdf-checkbox');
            if (!cb) return;
            this.updateGenerateBtn(cb.closest('#files_box'));
        });

        // "Generate PDF" btn → open order modal instead of merging directly.
        document.addEventListener('click', (e) => {
            const btn = e.target.closest('#generate_pdf_btn');
            if (!btn) return;
            e.preventDefault();
            this.openOrderModal(btn);
        });

        // Order modal: close on overlay / cancel / X.
        document.addEventListener('click', (e) => {
            if (!e.target.closest('#merge_pdf_order_modal')) return;
            if (e.target.closest('#merge_pdf_order_confirm')) return;
            if (e.target.closest('.close_modal') || e.target.closest('.cancel_button') || e.target.classList.contains('overlay')) {
                e.preventDefault();
                this.closeOrderModal();
            }
        });

        // Order modal: confirm → merge.
        document.addEventListener('click', (e) => {
            if (e.target.id !== 'merge_pdf_order_confirm') return;
            e.preventDefault();
            this.mergeFromModal();
        });

        return this;
    }

    updateGenerateBtn(filesBox) {
        if (!filesBox) return;
        const btn     = filesBox.querySelector('#generate_pdf_btn');
        const checked = filesBox.querySelectorAll('.file-pdf-checkbox:checked');
        if (!btn) return;
        btn.style.display = checked.length > 0 ? '' : 'none';
    }

    openOrderModal(generateBtn) {
        const filesBox  = generateBtn.closest('#files_box');
        const requestId = generateBtn.dataset.requestId;
        const checkboxes = Array.from(filesBox?.querySelectorAll('.file-pdf-checkbox:checked') ?? []);

        if (!checkboxes.length) {
            showToast('Please select at least one file.', 'error');
            return;
        }

        const modal = document.getElementById('merge_pdf_order_modal');
        if (!modal) {
            // Fallback: no modal in DOM, merge directly.
            this._mergeFiles(requestId, checkboxes.map(cb => cb.value), filesBox, checkboxes, generateBtn);
            return;
        }

        // Store context on the modal element for later use.
        modal._mergeRequestId  = requestId;
        modal._mergeFilesBox   = filesBox;
        modal._mergeCheckboxes = checkboxes;
        modal._mergeSourceBtn  = generateBtn;

        // Populate sortable list.
        const list = modal.querySelector('#merge_pdf_sortable');
        list.innerHTML = '';
        checkboxes.forEach(cb => {
            const fileItem = cb.closest('.files_list_item');
            const name     = fileItem?.querySelector('.file_name')?.textContent?.trim() || `File #${cb.value}`;
            const li       = document.createElement('li');
            li.className   = 'generate-docs-list__item';
            li.draggable   = true;
            li.dataset.attachmentId = cb.value;
            li.innerHTML   = `
                <svg class="generate-docs-list__drag-handle" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M9 5m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"/><path d="M9 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"/><path d="M9 19m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"/><path d="M15 5m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"/><path d="M15 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"/><path d="M15 19m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"/></svg>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M14 3v4a1 1 0 0 0 1 1h4"/><path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2"/></svg>
                ${name}`;
            list.appendChild(li);
        });

        this._initSortable(list);
        this._setModalLoading(modal, false);
        modal.style.display = 'block';
        requestAnimationFrame(() => { modal.style.opacity = 1; });
    }

    closeOrderModal() {
        const modal = document.getElementById('merge_pdf_order_modal');
        if (!modal) return;
        modal.style.opacity = 0;
        setTimeout(() => { modal.style.display = 'none'; }, 200);
    }

    async mergeFromModal() {
        const modal = document.getElementById('merge_pdf_order_modal');
        if (!modal) return;

        const orderedIds = Array.from(modal.querySelectorAll('#merge_pdf_sortable [data-attachment-id]'))
            .map(li => li.dataset.attachmentId);

        const { _mergeRequestId: requestId, _mergeFilesBox: filesBox, _mergeCheckboxes: checkboxes } = modal;

        const confirmBtn = modal.querySelector('#merge_pdf_order_confirm');
        this._setModalLoading(modal, true);
        confirmBtn.disabled = true;

        try {
            await this._mergeFiles(requestId, orderedIds, filesBox, checkboxes, null, modal);
        } finally {
            this._setModalLoading(modal, false);
            confirmBtn.disabled = false;
        }
    }

    async _mergeFiles(requestId, attachmentIds, filesBox, checkboxes, triggerBtn = null, modal = null) {
        const originalHtml  = triggerBtn?.innerHTML;
        const originalStyle = triggerBtn?.style.cssText;
        if (triggerBtn) {
            triggerBtn.disabled = true;
            triggerBtn.style.cssText += '; display:inline-flex; align-items:center; justify-content:center; min-width:120px;';
            triggerBtn.innerHTML = '<span class="btn-spinner btn-spinner--dark"></span>';
        }

        try {
            const params = new URLSearchParams({
                action:     'docuswift_generate_merged_pdf',
                nonce:      docuswiftPortal.generatePdfNonce,
                request_id: requestId,
            });
            attachmentIds.forEach(id => params.append('attachment_ids[]', id));

            const res  = await fetch(docuswiftPortal.ajax_url, {
                method:  'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body:    params.toString(),
            });
            const data = await res.json();

            if (data.success) {
                showToast(data.data.message || 'PDF generated!', 'success');
                if (modal) this.closeOrderModal();

                document.dispatchEvent(new CustomEvent('docuswift:request-facility-applied', {
                    detail: { requestId },
                }));

                setTimeout(() => {
                    const previewBtn = document.createElement('button');
                    previewBtn.dataset.docxPreviewUrl  = data.data.download_url;
                    previewBtn.dataset.docxPreviewName = data.data.filename;
                    previewBtn.style.display = 'none';
                    document.body.appendChild(previewBtn);
                    previewBtn.click();
                    previewBtn.remove();
                }, 100);

                checkboxes.forEach(cb => { cb.checked = false; });
                this.updateGenerateBtn(filesBox);
            } else {
                showToast(data.data?.message || 'Failed to generate PDF.', 'error');
            }
        } catch (err) {
            console.error(err);
            showToast('Network error. Please try again.', 'error');
        } finally {
            if (triggerBtn) {
                triggerBtn.disabled   = false;
                triggerBtn.style.cssText = originalStyle;
                triggerBtn.innerHTML  = originalHtml;
            }
        }
    }

    _initSortable(list) {
        let dragged = null;
        list.querySelectorAll('.generate-docs-list__item').forEach(item => {
            item.addEventListener('dragstart', () => {
                dragged = item;
                setTimeout(() => item.classList.add('dragging'), 0);
            });
            item.addEventListener('dragend', () => {
                item.classList.remove('dragging');
                list.querySelectorAll('.generate-docs-list__item').forEach(i => i.classList.remove('drag-over'));
                dragged = null;
            });
            item.addEventListener('dragover', e => {
                e.preventDefault();
                if (dragged && dragged !== item) {
                    list.querySelectorAll('.generate-docs-list__item').forEach(i => i.classList.remove('drag-over'));
                    item.classList.add('drag-over');
                }
            });
            item.addEventListener('drop', e => {
                e.preventDefault();
                if (dragged && dragged !== item) {
                    const items   = [...list.querySelectorAll('.generate-docs-list__item')];
                    const fromIdx = items.indexOf(dragged);
                    const toIdx   = items.indexOf(item);
                    list.insertBefore(dragged, fromIdx < toIdx ? item.nextSibling : item);
                }
                item.classList.remove('drag-over');
            });
        });
    }

    _setModalLoading(modal, isLoading) {
        const loader = modal?.querySelector('.modal-loader');
        if (!loader) return;
        if (isLoading) {
            loader.removeAttribute('hidden');
            loader.setAttribute('aria-hidden', 'false');
        } else {
            loader.setAttribute('hidden', '');
            loader.setAttribute('aria-hidden', 'true');
        }
    }
}
